import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { datamodelSubTypeQuery } from '@queries';
import { ButtonSpinnerDarkIcon } from '@svgs';
import SortableSelect from '../LocationsField/SortableSelect/SortableSelect.jsx';

const formatDatamodelSubTypeOptions = (
  defaultOptions,
  datamodelSubTypeData,
) => {
  let objects = defaultOptions;
  if (datamodelSubTypeData) {
    // combine the lists
    //
    // exclude items already on the default list
    const newObjects = datamodelSubTypeData.objects.filter(
      (item) => !objects.find((i) => i.id === item.id),
    );
    objects = [...objects, ...newObjects];
  }
  return objects.map((datamodelSubType) => {
    return { value: datamodelSubType.id, label: datamodelSubType.name };
  });
};

const normalizeFieldValueIds = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.map((item) => (typeof item === 'object' ? item.id : item));
};

const DatamodelSubTypeFieldField = ({ field, defaultOptions = [] }) => {
  // based on:
  // https://github.com/kkomelin/react-select-customization-examples/blob/main/src/components/ExampleRemoteDataDebouncingAndCaching.tsx
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');

  // get datamodel sub types via live search
  const { data: datamodelSubTypeData, isPending: datamodelSubTypeIsPending } =
    useQuery(
      datamodelSubTypeQuery(
        {
          datamodel_sub_type: 'page_tag',
          limit: 10,
          order_by: 'name',
          q: searchText,
        },
        {
          enabled: !!searchText,
        },
      ),
    );

  // combine and format the options for the field
  const combinedOptions = formatDatamodelSubTypeOptions(
    defaultOptions,
    datamodelSubTypeData,
  );

  const handleSearchDebounced = useRef(
    debounce((searchText) => setSearchText(searchText), 300),
  ).current;

  const noOptionsMessage = (obj) => {
    if (obj.inputValue.trim().length === 0) {
      return null;
    }
    return 'No matching tags items';
  };

  const handleInputChange = (inputText, meta) => {
    if (meta.action !== 'input-blur' && meta.action !== 'menu-close') {
      setInputText(inputText);
      handleSearchDebounced(inputText);
    }
  };

  return (
    <SortableSelect
      name={field.name}
      inputRef={field.ref}
      options={combinedOptions}
      isClearable={true}
      className="basic-multi-select"
      classNamePrefix="select"
      isLoading={!!searchText && datamodelSubTypeIsPending}
      inputValue={inputText}
      onInputChange={handleInputChange}
      noOptionsMessage={noOptionsMessage}
      onChange={(val) => field.onChange(val ? val.map((v) => v.value) : [])}
      fieldValue={normalizeFieldValueIds(field.value)}
    />
  );
};

// wrapper around the field so we dont render the field
// until we try to load all the items first
//
// might be able to refactor in future to remove this requirement
export const DatamodelSubTypeField = ({ field }) => {
  // get datamodel sub types that are already selected / available
  const { data: datamodelSubTypeData, isPending: datamodelSubTypeIsPending } =
    useQuery(
      datamodelSubTypeQuery({
        datamodel_sub_type: 'page_tag',
        limit: 50,
        order_by: 'name',
      }),
    );

  const isLoading = datamodelSubTypeIsPending;

  return (
    <>
      {isLoading && <ButtonSpinnerDarkIcon />}
      {!isLoading && (
        <DatamodelSubTypeFieldField
          field={field}
          defaultOptions={
            datamodelSubTypeData ? datamodelSubTypeData.objects : []
          }
        />
      )}
    </>
  );
};
